/**
 * TMS 소스 타일의 순수 규약 — URL·동시성 제한·4분면 점유 마스크·합성 격자 좌표. DOM·Worker 어느 쪽에도 의존하지 않아
 * 메인 스레드 합성기와 Worker 합성기가 같은 코드로 같은 결과를 낸다(spec inspection3d-tms-imagery 「타일 합성과 점유
 * 마스크 산출은 UI 스레드 밖에서 한다」).
 */
import type { TileRange } from "./webMercatorTiles";

export const SOURCE_TILE_PX = 256;
/** 스펙 「fetch 동시성 제한」·「동시 요청 수는 바꾸지 않는다」의 값 */
export const FETCH_CONCURRENCY = 200;
export const FULL_QUADRANT_MASK = 0b1111;

export type Limiter = <T>(fn: () => Promise<T>) => Promise<T>;

export function createLimiter(limit: number): Limiter {
  let active = 0;
  const queue: (() => void)[] = [];
  const pump = () => {
    if (active >= limit) return;
    const task = queue.shift();
    if (!task) return;
    active++;
    task();
  };
  return function run<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      queue.push(() => {
        fn()
          .then(resolve, reject)
          .finally(() => {
            active--;
            pump();
          });
      });
      pump();
    });
  };
}

// geotiff 타일 버킷 경로 규약의 공통 prefix.
// 2D(OpenLayers {z}/{x}/{y} 템플릿)와 3D(buildTmsTileUrl reverseY 계산)가 같은 버킷
// 경로를 가리키도록 prefix 생성을 한 곳으로 모은다. 버킷 규약이 바뀌면 여기만 고친다.
export function buildGeotiffTilePrefix(
  baseUrl: string,
  projectId: number,
  layerName: string
): string {
  return `${baseUrl}etc/${projectId}/geotiff_tiles/${layerName}/`;
}

export function buildTmsTileUrl(
  baseUrl: string,
  projectId: number,
  layerName: string,
  level: number,
  x: number,
  y: number
): string {
  const reverseY = Math.pow(2, level) - 1 - y;
  return `${buildGeotiffTilePrefix(baseUrl, projectId, layerName)}${level}/${x}/${reverseY}.png`;
}

/**
 * 타일 하나의 점유 결과 — `mask` 는 4분면 비트(bit0 좌상·bit1 우상·bit2 좌하·bit3 우하), `null` 은 서버가
 * 타일을 주지 않아(404/403) 빈 타일로 확정된 것. 네트워크 오류는 점유를 모르는 것이라 결과를 내지 않는다.
 */
export interface TileOccupancy {
  level: number;
  x: number;
  y: number;
  mask: number | null;
}

/** RGBA 픽셀 배열(한 변 `size`)에서 알파가 0 이 아닌 픽셀이 있는 4분면의 비트를 모은다 */
export function quadrantMaskFromRgba(
  data: ArrayLike<number>,
  size: number
): number {
  const half = size / 2;
  let mask = 0;
  for (let py = 0; py < size; py++) {
    const rowBase = py * size * 4;
    const rowBit = py < half ? 0 : 2;
    for (let px = 0; px < size; px++) {
      if (data[rowBase + px * 4 + 3] === 0) continue;
      mask |= 1 << (rowBit + (px < half ? 0 : 1));
      if (mask === FULL_QUADRANT_MASK) return mask;
    }
  }
  return mask;
}

export interface TileXY {
  x: number;
  y: number;
}

/** 소스 범위 안 타일 `(x, y)` 가 합성 캔버스에서 차지하는 좌상단 픽셀 */
export function sourceTileCanvasOffset(
  range: TileRange,
  tile: TileXY
): { px: number; py: number } {
  return {
    px: (tile.x - range.minX) * SOURCE_TILE_PX,
    py: (tile.y - range.minY) * SOURCE_TILE_PX,
  };
}

export function sourceRangeCanvasSize(range: TileRange): {
  width: number;
  height: number;
} {
  return {
    width: (range.maxX - range.minX + 1) * SOURCE_TILE_PX,
    height: (range.maxY - range.minY + 1) * SOURCE_TILE_PX,
  };
}
